import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom';
import MainLayout from 'layout/MainLayout';
import AgencyActivitiesSettings from 'pages/agencyActivitiesSettings/AgencyActivitiesSettings';
import {
  BrandingPage,
  CampaignPage,
  AgenciesPage,
  AgencyDetailsPage,
  MarketingPage,
  StaffLicensePage,
  StaffMemberPage,
  ManageCampaign,
  ContractPage,
  PropertyDetailsPage,
  TenancyInformationPage,
  VendorDetailsPage,
  VendorIdentityVerification,
  Permissions,
  IntegrationsPage,
  CrmPage,
  StaffReporting,
  CampaignMarketing,
  CampaignMarketingDetails,
  MarketingDashboard,
  MarketingEditPackagePage,
  MarketingItemsPage,
  MarketingNewPackagePage,
  MarketingSuppliersPage,
  CampaignsDashboard,
  Contracts,
  ContractsDashboard,
  ListingAgreement,
  EmailTemplates,
  ContractSetting,
  Partner,
  Network,
  ListingsPage,
  PartnershipPage,
  Submissions,
  ContractFiles,
  ActivityLogView,
  BuyersInformation,
  BuyersSolicitor,
  BuyersAgent,
  BuyerIdentityVerification,
  TasksPage,
  SaleInformation,
  Conditions,
  Settlement,
  Deposits,
  SpecialConditions,
  Finance,
  Commission,
  Referral,
  Vpa,
  ServiceFee,
  FeatureToggles,
} from 'pages';
import { PageNotFoundPage, UnexpectedErrorPage } from 'pages/errorPages';
import { FormType } from '@urbanx/agx.campaigns.client';
import { MenuTitles, Pages } from './menu';
import ProtectedRoute from 'layout/ProtectedRoute';
import Discrepancies from 'pages/discrepancies/discrepancies';
import { StaffRole } from '@urbanx/agx.auth.client';
import CampaignOverview from 'pages/campaigns/components/campaignOverview/CampaignOverview';
import CampaignTasks from 'pages/campaigns/components/campaignTasks/CampaignTasks';
import TeamPageLayout from 'pages/team/TeamPageLayout';
import TeamMembersPage from 'pages/team/TeamMembersPage/TeamMembersPage';
import AddOrEditTeamMemberPage from 'pages/team/AddOrEditTeamMemberPage/AddOrEditTeamMemberPage';
import ManageTeamsPage from 'pages/team/ManageTeamsPage/ManageTeamsPage';
import AddOrEditTeamPage from 'pages/team/AddOrEditTeamPage/AddOrEditTeamPage';
import ManageUrbanXTeamPage from 'pages/team/ManageUrbanXTeamPage/ManageUrbanXTeamPage';
import AgencyPage from 'pages/agency/AgencyPage';
import ContractTasks from 'pages/contracts/components/ContractTasks';
import ContractInformationLayout from 'pages/contracts/components/contract-information/ContractInformationLayout';
import SupportPage from 'pages/support/SupportPage';
import SupportTools from 'pages/support/SupportTools';
import PoisonQueues from 'pages/support/PoisonQueues/PoisonQueues';
import PoisonQueue from 'pages/support/PoisonQueue/PoisonQueue';
import PoisonQueueMessage from 'pages/support/PoisonQueue/PoisonQueueMessage';
import Migrations from 'pages/support/Migrations/Migrations';
import UpdateCampaignStatus from 'pages/support/UpdateCampaignStatus/UpdateCampaignStatus';
import RexDisconnection from 'pages/support/RexDisconnection/RexDisconnection';
import TaskProgressor from 'pages/support/TaskProgressor/TaskProgressor';
import UpdateLegalName from 'pages/support/UpdateLegalName/UpdateLegalName';
import CrmSupport from 'pages/support/CrmSupport/CrmSupport';
import AgentIntegrations from 'pages/support/AgentIntegrations/AgentIntegrations';
import RestoreSoftDeletedAgent from 'pages/support/RestoreSoftDeletedAgent/RestoreSoftDeletedAgent';
import LeaderboardVisibility from 'pages/support/LeaderboardVisibility/LeaderboardVisibility';
import ManualDocuSignUpdates from 'pages/support/ManualDocuSignUpdates/ManualDocuSignUpdates';
import ReportingPage from 'pages/reportingPage/reportingPage';
import MonthEndPage from 'pages/reportingPage/monthEnd/MonthEndPage';
import PartnersPage from 'pages/reportingPage/pages/PartnersPage';
import PartnerPage from 'pages/reportingPage/pages/pages/PartnerPage';
import IndividualsPage from 'pages/reportingPage/pages/pages/IndividualsPage';
import LeaderboardPage from 'pages/reportingPage/pages/pages/LeaderboardPage';
import TasksDashboardPage from 'pages/tasksDashboard/TasksDashboardPage';
import EditStaffMember from 'pages/staff/EditStaffMember';
import CreateStaffMember from 'pages/staff/CreateStaffMember';
import AutomationErrorsPage from 'pages/automation-errors/AutomationErrorsPage';

const routes: RouteObject[] = [
  {
    path: Pages.DASHBOARD,
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: <TasksDashboardPage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.TASKS_DASHBOARD,
        element: <TasksDashboardPage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.ALL_TASKS,
        element: <TasksPage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.AUTOMATION_ERRORS,
        element: (
          <ProtectedRoute
            element={<AutomationErrorsPage />}
            requiredRoles={[StaffRole.SuperAdmin, StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.CAMPAIGNS,
        element: <CampaignsDashboard />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.CONTRACTS,
        element: <ContractsDashboard />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.AGENCIES,
        element: <AgenciesPage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: `${Pages.AGENCIES}/:agencyId`,
        element: <AgencyPage />,
        errorElement: <UnexpectedErrorPage />,
        children: [
          {
            path: Pages.CAMPAIGNS,
            element: <CampaignPage />,
            errorElement: <UnexpectedErrorPage />,
          },

          {
            path: `${Pages.CAMPAIGNS}/:campaignId`,
            element: <Outlet />,
            errorElement: <UnexpectedErrorPage />,
            children: [
              {
                index: true,
                element: <Navigate to={Pages.CAMPAIGN_OVERVIEW} replace />,
              },
              {
                path: Pages.CAMPAIGN_OVERVIEW,
                element: <CampaignOverview />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.CAMPAIGN_ERRORS,
                element: <AutomationErrorsPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.CAMPAIGN_TASKS,
                element: <CampaignTasks />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.PROPERTY_INFORMATION,
                element: <ManageCampaign formType={FormType.BlankContract} />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.PROPERTY_INFORMATION_SECTION,
                element: <Outlet />,
                errorElement: <UnexpectedErrorPage />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="property-details" replace />,
                  },
                  {
                    path: 'property-details',
                    element: <PropertyDetailsPage />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'vendor-details',
                    element: <VendorDetailsPage />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'contract',
                    element: <ContractPage />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'tenancy-information',
                    element: <TenancyInformationPage />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'identity-verification',
                    element: <VendorIdentityVerification />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                ],
              },
              {
                path: Pages.FINANCE,
                element: <Finance />,
                errorElement: <UnexpectedErrorPage />,
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate
                        to={Pages.COMMISSION_COMMISSION}
                        replace
                        state={{ activeTab: MenuTitles.FINANCE }}
                      />
                    ),
                  },
                  {
                    path: Pages.COMMISSION_COMMISSION,
                    element: <Commission />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.COMMISSION_SERVICE_FEE,
                    element: <ServiceFee />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.COMMISSION_REFERRAL,
                    element: <Referral />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'vpa',
                    element: <Vpa />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                ],
              },
              {
                path: Pages.CAMPAIGN_MARKETING,
                element: <CampaignMarketing />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.CONTRACTS,
                element: <Contracts />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.ACTIVITY,
                element: <ActivityLogView />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.FILES,
                element: <Submissions />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.PERMISSIONS,
                element: <Permissions />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.CAMPAIGN_MARKETING}/:marketingPackageId`,
                element: <CampaignMarketingDetails />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.CONTRACTS}/:contractId`,
                element: <Outlet />,
                errorElement: <UnexpectedErrorPage />,
                children: [
                  {
                    path: Pages.CONTRACT_TASKS,
                    element: <ContractTasks />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.BUYERS_INFORMATION,
                    element: <BuyersInformation />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.BUYERS_SOLICITOR,
                    element: <BuyersSolicitor />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.BUYERS_AGENT,
                    element: <BuyersAgent />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.BUYERS_IDENTITY_VERIFICATION,
                    element: <BuyerIdentityVerification />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    element: <ContractInformationLayout />,
                    errorElement: <UnexpectedErrorPage />,
                    children: [
                      {
                        path: Pages.SALE_INFORMATION,
                        element: <SaleInformation />,
                        errorElement: <UnexpectedErrorPage />,
                      },
                      {
                        path: Pages.CONDITIONS,
                        element: <Conditions />,
                        errorElement: <UnexpectedErrorPage />,
                      },
                      {
                        path: Pages.SPECIAL_CONDITIONS,
                        element: <SpecialConditions />,
                        errorElement: <UnexpectedErrorPage />,
                      },
                      {
                        path: Pages.SETTLEMENT,
                        element: <Settlement />,
                        errorElement: <UnexpectedErrorPage />,
                      },
                      {
                        path: Pages.DEPOSITS,
                        element: <Deposits />,
                        errorElement: <UnexpectedErrorPage />,
                      },
                    ],
                  },
                  {
                    path: Pages.FILES,
                    element: <ContractFiles />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                ],
              },
            ],
          },
          {
            path: Pages.PROPOSALS,
            lazy: () => import('pages/proposalBuilder/ProposalsRoute'),
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: `${Pages.PROPOSALS}/new`,
            lazy: () => import('pages/proposalBuilder/ProposalTemplateEditorRoute'),
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: `${Pages.PROPOSALS}/sections/:sectionId`,
            lazy: () => import('pages/proposalBuilder/SectionTemplateEditorRoute'),
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: `${Pages.PROPOSALS}/:templateId`,
            lazy: () => import('pages/proposalBuilder/ProposalTemplateEditorRoute'),
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.WEBSITE_LISTINGS,
            element: <ListingsPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.MARKETING,
            element: <MarketingPage />,
            errorElement: <UnexpectedErrorPage />,
            children: [
              {
                index: true,
                element: <MarketingDashboard />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: 'items',
                element: <MarketingItemsPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: 'suppliers',
                element: <MarketingSuppliersPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: 'package',
                element: <Navigate to="new" replace />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: 'package/new',
                element: <MarketingNewPackagePage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: 'package/:marketingPackageId',
                element: <MarketingEditPackagePage />,
                errorElement: <UnexpectedErrorPage />,
              },
            ],
          },
          {
            path: Pages.TEAMS,
            element: <TeamPageLayout />,
            errorElement: <UnexpectedErrorPage />,
            children: [
              {
                index: true,
                element: <Navigate to={Pages.TEAMS_MEMBERS} replace />,
              },
              {
                path: Pages.TEAMS_MEMBERS,
                element: <TeamMembersPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.TEAMS_MEMBERS}/add`,
                element: <AddOrEditTeamMemberPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.TEAMS_MEMBERS}/edit/:agentId`,
                element: <AddOrEditTeamMemberPage isEdit />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.TEAMS_MANAGE_TEAMS,
                element: <ManageTeamsPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.TEAMS_MANAGE_TEAMS}/add`,
                element: <AddOrEditTeamPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.TEAMS_MANAGE_TEAMS}/edit/:teamId`,
                element: <AddOrEditTeamPage isEdit />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.TEAMS_URBANX_TEAM,
                element: <ManageUrbanXTeamPage />,
                errorElement: <UnexpectedErrorPage />,
              },
            ],
          },
          {
            path: `${Pages.PARTNERSHIP}/*`,
            element: (
              <ProtectedRoute
                element={<PartnershipPage />}
                requiredRoles={[
                  StaffRole.BusinessManager,
                  StaffRole.SuperAdmin,
                  StaffRole.Executive,
                ]}
              />
            ),
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.AGENCY_DETAILS,
            element: <AgencyDetailsPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.AGENCY_ACTIVITIES,
            element: <AgencyActivitiesSettings />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.BRANDING,
            element: <BrandingPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.INTEGRATIONS,
            element: <IntegrationsPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.CRM,
            element: <CrmPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.LISTING_AGREEMENT,
            element: <ListingAgreement />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.CONTRACT_SETTING,
            element: <ContractSetting />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.EMAIL_TEMPLATES,
            element: <EmailTemplates />,
            errorElement: <UnexpectedErrorPage />,
          },
        ],
      },

      {
        path: Pages.STAFF,
        element: <StaffMemberPage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.EDIT_STAFF,
        element: <Navigate to={Pages.STAFF} replace />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: `${Pages.EDIT_STAFF}/:userId`,
        element: <EditStaffMember />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.CREATE_STAFF,
        element: <CreateStaffMember />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.STAFF_LICENSE,
        element: <StaffLicensePage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: 'executive-report',
        element: (
          <ProtectedRoute element={<StaffReporting />} requiredRoles={[StaffRole.Executive]} />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: 'network',
        element: <ProtectedRoute element={<Network />} requiredRoles={[StaffRole.Executive]} />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: 'discrepancies',
        element: (
          <ProtectedRoute
            element={<Discrepancies />}
            requiredRoles={[StaffRole.SuperAdmin, StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: `${Pages.REPORTING_PAGE}/${Pages.REPORTING_MONTH_END}`,
        element: (
          <ProtectedRoute
            element={<MonthEndPage />}
            requiredRoles={[StaffRole.BusinessManager, StaffRole.SuperAdmin, StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.REPORTING_PAGE,
        element: (
          <ProtectedRoute
            element={<ReportingPage />}
            requiredRoles={[StaffRole.BusinessManager, StaffRole.SuperAdmin, StaffRole.Executive]}
          />
        ),
        children: [
          {
            index: true,
            element: <Navigate to={Pages.REPORTING_PARTNERS} replace />,
          },
          {
            path: Pages.REPORTING_PARTNERS,
            element: <PartnersPage />,
            children: [
              {
                index: true,
                element: <Navigate to={Pages.REPORTING_PARTNERS_PARTNER} replace />,
              },
              {
                path: Pages.REPORTING_PARTNERS_PARTNER,
                element: <PartnerPage />,
              },
              {
                path: `${Pages.REPORTING_PARTNERS_PARTNER}/:agencyId`,
                element: <Partner />,
              },
              {
                path: Pages.REPORTING_PARTNERS_INDIVIDUALS,
                element: <IndividualsPage />,
              },
              {
                path: `${Pages.REPORTING_PARTNERS_INDIVIDUALS}/:agentId`,
                element: <Partner individual />,
              },
              {
                path: Pages.REPORTING_PARTNERS_LEADERBOARD,
                element: <LeaderboardPage />,
              },
            ],
          },
        ],
      },
      {
        path: Pages.TASKS_OVERVIEW,
        lazy: async () => ({
          Component: (await import('pages/tasks-overview/TasksOverviewPage')).default,
        }),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.FEATURE_TOGGLES,
        element: (
          <ProtectedRoute
            element={<FeatureToggles />}
            requiredRoles={[StaffRole.SuperAdmin, StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.SUPPORT,
        element: (
          <ProtectedRoute
            element={<SupportPage />}
            requiredRoles={[StaffRole.SuperAdmin, StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
        children: [
          {
            index: true,
            element: <SupportTools />,
          },
          {
            path: Pages.SUPPORT_QUEUES,
            errorElement: <UnexpectedErrorPage />,
            children: [
              {
                index: true,
                element: <PoisonQueues />,
              },
              {
                path: ':service/:queueName',
                children: [
                  {
                    index: true,
                    element: <PoisonQueue />,
                  },
                  {
                    path: ':messageId',
                    element: <PoisonQueueMessage />,
                  },
                ],
              },
            ],
          },
          {
            path: Pages.SUPPORT_MIGRATIONS,
            element: <Migrations />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_UPDATE_LEGAL_NAME,
            element: <UpdateLegalName />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_UPDATE_CAMPAIGN_STATUS,
            element: <UpdateCampaignStatus />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_REX_DISCONNECTION,
            element: <RexDisconnection />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_TASK_PROGRESSOR,
            element: <TaskProgressor />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_CRM,
            element: <CrmSupport />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_AGENT_INTEGRATIONS,
            element: <AgentIntegrations />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_RESTORE_SOFT_DELETED_AGENT,
            element: <RestoreSoftDeletedAgent />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_LEADERBOARD_VISIBILITY,
            element: <LeaderboardVisibility />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.SUPPORT_MANUAL_DOCUSIGN_UPDATES,
            element: (
              <ProtectedRoute
                element={<ManualDocuSignUpdates />}
                requiredRoles={[StaffRole.SuperAdmin]}
              />
            ),
            errorElement: <UnexpectedErrorPage />,
          },
        ],
      },
      {
        path: '*',
        element: <PageNotFoundPage />,
        errorElement: <UnexpectedErrorPage />,
      },
    ],
  },
];

const router = createBrowserRouter(routes, { basename: import.meta.env.BASE_URL });

export default router;
